/**
 * Pure helpers for the LMS provisioning jobs UI (CC-14598). No I/O — safe to
 * unit test in isolation. Mirrors the portal lms_job_status / lms_job_type
 * domain so disabled-states and progress match the backend.
 */

export const JOB_STATUS = {
  PENDING: 'PENDING',
  QUEUED: 'QUEUED',
  RUNNING: 'RUNNING',
  COMPLETED: 'COMPLETED',
  COMPLETED_WITH_ERRORS: 'COMPLETED_WITH_ERRORS',
  FAILED: 'FAILED',
};

export const JOB_TYPE = {
  LEARNER_BULK_PROVISION: 'LEARNER_BULK_PROVISION',
  LOCATION_BULK_PROVISION: 'LOCATION_BULK_PROVISION',
};

export const BULK_SCOPE = {
  ALL: 'ALL',
  FACILITIES: 'FACILITIES',
};

const TERMINAL_STATUSES = new Set([
  JOB_STATUS.COMPLETED,
  JOB_STATUS.COMPLETED_WITH_ERRORS,
  JOB_STATUS.FAILED,
]);

/** A job is terminal when no further progress is expected. */
export const isTerminalStatus = (status) => TERMINAL_STATUSES.has(status);

/** Red/Amber/Green tier for a job status, for badge colouring. */
export const statusTier = (status) => {
  switch (status) {
    case JOB_STATUS.COMPLETED:
      return 'success';
    case JOB_STATUS.COMPLETED_WITH_ERRORS:
      return 'warning';
    case JOB_STATUS.FAILED:
      return 'error';
    case JOB_STATUS.RUNNING:
    case JOB_STATUS.QUEUED:
    case JOB_STATUS.PENDING:
    default:
      return 'info';
  }
};

/** Progress percentage (0–100) from processed/total, clamped and integer. */
export const jobProgressPercent = (job) => {
  const total = Number(job?.totalRecords) || 0;
  if (total <= 0) {
    return isTerminalStatus(job?.status) ? 100 : 0;
  }
  const processed = Number(job?.processedRecords) || 0;
  const pct = Math.round((processed / total) * 100);
  return Math.max(0, Math.min(100, pct));
};

/**
 * True when the chain readiness payload (from
 * `GET /facilities/:id/provision-canvas`) is fully provisioned. Falls back
 * to whatever is set when the readiness payload hasn't been loaded yet.
 */
const chainReadyFromReadiness = (readiness) => Boolean(readiness?.chainReady);

/**
 * True when a facility is considered provisioned in Canvas. Prefers the
 * readiness snapshot when available (the backend already collapsed
 * `chainReady` into a single bool), falls back to:
 *   - explicit `canvasSubAccountId` on the row payload
 *   - `currentDeliveryLms === 'canvas'` (the live
 *     `/api/admin/location-delivery-lms` payload does not surface a
 *     sub-account id yet, but a facility can only be on Canvas delivery
 *     once provisioned, so it's a safe positive signal).
 */
export const isFacilityProvisionedInCanvas = (facility = {}, readiness = null) => {
  if (readiness?.facilityProvisioned) return true;
  if (facility.canvasSubAccountId) return true;
  const lms = String(facility.currentDeliveryLms || '').trim().toLowerCase();
  return lms === 'canvas';
};

/**
 * True when the entire ancestor chain (parent → … → facility) has a Canvas
 * sub-account. Drives the "Step 1" gate that unlocks learner provisioning
 * and the Switch-to-Canvas action.
 *
 * Two paths to "ready":
 *   1. The readiness payload from `GET /facilities/:id/provision-canvas`
 *      already says `chainReady: true` (the authoritative answer).
 *   2. Pre-readiness heuristic: the row itself reports the facility is on
 *      Canvas (explicit `canvasSubAccountId`, or `currentDeliveryLms ===
 *      'canvas'` because a facility cannot be on Canvas delivery without
 *      having been provisioned), and the parent is not explicitly flagged
 *      as unprovisioned.
 */
export const isProvisionChainReady = (facility = {}, readiness = null) => {
  if (chainReadyFromReadiness(readiness)) return true;
  if (!isFacilityProvisionedInCanvas(facility, readiness)) return false;
  return facility.parentProvisioned !== false;
};

/**
 * Whether the "Provision Existing Learners" action should be enabled for a
 * facility. Mirrors the backend precondition: the entire chain must be
 * Canvas-provisioned and no learner bulk job may currently be running.
 */
export const canProvisionLearners = (facility = {}, facilityJobs = [], readiness = null) => {
  if (!isFacilityProvisionedInCanvas(facility, readiness)) {
    return { enabled: false, reason: 'Provision the facility to Canvas first.' };
  }
  if (!isProvisionChainReady(facility, readiness)) {
    return { enabled: false, reason: 'Provision the parent facility hierarchy first.' };
  }
  const learnerJobRunning = facilityJobs.some(
    (job) => job?.jobType === JOB_TYPE.LEARNER_BULK_PROVISION && !isTerminalStatus(job?.status),
  );
  if (learnerJobRunning) {
    return { enabled: false, reason: 'A learner provisioning job is already running.' };
  }
  return { enabled: true, reason: '' };
};

/**
 * Whether the "Provision Facility" action should be enabled. Disabled once
 * the chain is fully provisioned (the click would be a no-op).
 */
export const canProvisionFacility = (facility = {}, readiness = null) => {
  if (isProvisionChainReady(facility, readiness)) {
    return { enabled: false, reason: 'Facility and its parent chain are already provisioned.' };
  }
  return { enabled: true, reason: '' };
};

/** Whether a grid row can be included in async bulk subaccount provisioning. */
export const canBulkProvisionFacility = (facility = {}) => canProvisionFacility(facility, null).enabled;

const RETRIABLE_STATUSES = new Set([JOB_STATUS.FAILED, JOB_STATUS.COMPLETED_WITH_ERRORS]);

/**
 * Whether a job can be retried. Mirrors the backend: only terminal jobs that
 * ended with failures (FAILED or COMPLETED_WITH_ERRORS) are retriable; a clean
 * COMPLETED job is not.
 */
export const canRetryJob = (job = {}) => RETRIABLE_STATUSES.has(job?.status);

/** Human-friendly summary line for a job row. */
export const summarizeJob = (job = {}) => {
  const success = Number(job.successCount) || 0;
  const failure = Number(job.failureCount) || 0;
  const total = Number(job.totalRecords) || 0;
  const failureSuffix = failure > 0 ? `, ${failure} failed` : '';
  return `${success}/${total} succeeded${failureSuffix}`;
};

const learnerProvisionJobs = (facilityJobs = []) =>
  facilityJobs.filter((job) => job?.jobType === JOB_TYPE.LEARNER_BULK_PROVISION);

const jobSortTimestamp = (job) => {
  const raw = job?.startedAt ?? job?.createdAt ?? 0;
  const ts = new Date(raw).getTime();
  return Number.isFinite(ts) ? ts : 0;
};

/** Most recent LEARNER_BULK_PROVISION job for the facility, if any. */
export const getLatestLearnerProvisionJob = (facilityJobs = []) => {
  const jobs = learnerProvisionJobs(facilityJobs);
  if (jobs.length === 0) return null;
  return [...jobs].sort((a, b) => jobSortTimestamp(b) - jobSortTimestamp(a))[0];
};

const isCleanLearnerProvisionJob = (job) =>
  job?.jobType === JOB_TYPE.LEARNER_BULK_PROVISION &&
  job?.status === JOB_STATUS.COMPLETED &&
  (Number(job?.failureCount) || 0) === 0;

/**
 * True when at least one `LEARNER_BULK_PROVISION` job completed with zero
 * record failures. Partial runs (COMPLETED_WITH_ERRORS) and FAILED jobs do
 * not satisfy the cutover precondition — the operator should retry first.
 */
export const areExistingLearnersProvisioned = (facilityJobs = []) =>
  facilityJobs.some(isCleanLearnerProvisionJob);

/**
 * True when the latest learner bulk job ended with failures (terminal FAILED
 * or COMPLETED_WITH_ERRORS, or a non-zero failureCount on a terminal job).
 */
export const hasLearnerProvisionFailures = (facilityJobs = []) => {
  const latest = getLatestLearnerProvisionJob(facilityJobs);
  if (!latest || !isTerminalStatus(latest.status)) return false;
  if (latest.status === JOB_STATUS.FAILED || latest.status === JOB_STATUS.COMPLETED_WITH_ERRORS) {
    return true;
  }
  return (Number(latest.failureCount) || 0) > 0;
};

/** Whether a job row should offer per-record error drill-down. */
export const canViewJobErrors = (job = {}) =>
  job?.status === JOB_STATUS.FAILED || job?.status === JOB_STATUS.COMPLETED_WITH_ERRORS;

const firstTruthyString = (...values) => {
  for (const value of values) {
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      return String(value);
    }
  }
  return '—';
};

const extractJobErrorRows = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.errors)) return payload.errors;
  if (Array.isArray(payload?.records)) return payload.records;
  return [];
};

/**
 * Normalizes the `/lms/jobs/{id}/errors` payload into a flat list of
 * `{ recordLabel, message }` rows for the UI.
 */
export const normalizeJobErrors = (payload) => {
  const rows = extractJobErrorRows(payload);

  return rows.map((row, index) => ({
    recordLabel: firstTruthyString(
      row?.recordKey,
      row?.recordId,
      row?.identifier,
      row?.learnerId,
      row?.locationId,
      `Record ${index + 1}`,
    ),
    message: firstTruthyString(row?.errorMessage, row?.message, row?.error, 'Unknown error'),
  }));
};

/**
 * Two preconditions must be satisfied before an operator can flip a
 * facility's delivery LMS to Canvas:
 *
 *   1. **Provision Facility** — the facility *and every ancestor* has a
 *      Canvas sub-account. The "Provision Facility" button walks the chain
 *      itself, so the parent step is folded into this single item; the chain
 *      detail is rendered separately as per-location indicators.
 *   2. **Provision Existing Learners** — at least one
 *      LEARNER_BULK_PROVISION run completed cleanly (COMPLETED with zero
 *      failures).
 *
 * Returns `{ ready, checklist }` for the dialog's checklist + button gate.
 */
export const computeSwitchToCanvasReadiness = (facility = {}, facilityJobs = [], readiness = null) => {
  const facilityDone = isProvisionChainReady(facility, readiness);
  const learnersDone = areExistingLearnersProvisioned(facilityJobs);

  const facilityDetail = facilityDone
    ? 'Facility and parent locations are provisioned in Canvas.'
    : 'Click "Provision Facility" — this provisions the parent location(s) and the facility itself.';

  const learnersDetail = (() => {
    if (learnersDone) return 'Existing learners have been provisioned at least once.';
    if (hasLearnerProvisionFailures(facilityJobs)) {
      const latest = getLatestLearnerProvisionJob(facilityJobs);
      const failureCount = Number(latest?.failureCount) || 0;
      if (failureCount > 0) {
        return `${failureCount} learner${failureCount === 1 ? '' : 's'} failed — review job errors and retry before switching.`;
      }
      return 'Learner provisioning failed — review job errors and retry before switching.';
    }
    if (!facilityDone) return 'Available after the facility is provisioned.';
    return 'Run "Provision Existing Learners" so live learners exist in Canvas before switching delivery.';
  })();

  const checklist = [
    {
      key: 'facility',
      label: 'Provision Facility (parent + this facility)',
      done: facilityDone,
      detail: facilityDetail,
    },
    {
      key: 'learners',
      label: 'Provision the existing learners',
      done: learnersDone,
      detail: learnersDetail,
    },
  ];

  return {
    checklist,
    ready: checklist.every((item) => item.done),
  };
};

/**
 * Builds a small dataset for the per-location chain indicators rendered in
 * the provisioning panel. Each entry is a pre-computed view-model the
 * component can render without re-deriving conditions:
 *   - `key`             stable React key
 *   - `label`           "Facility" for the leaf, "Parent location" otherwise
 *   - `name`            location name (or short code as a fallback)
 *   - `shortCode`       short code badge text (may be null)
 *   - `provisioned`     final flag — has a Canvas sub-account
 *   - `slaExists`       intermediate flag — has an SLA but not yet a
 *                       sub-account (consumer is in flight)
 *   - `canvasSubAccountId`
 *
 * Returns an empty list when the readiness snapshot has not been loaded yet
 * (the panel falls back to row-level `parentProvisioned` instead).
 */
export const summarizeChain = (readiness) => {
  const chain = Array.isArray(readiness?.chain) ? readiness.chain : [];
  return chain.map((entry) => ({
    canvasSubAccountId: entry.canvasSubAccountId ?? null,
    key: `chain-${entry.locationId}`,
    label: entry.isFacility ? 'Facility' : 'Parent location',
    name: entry.locationName || entry.shortCode || `Location ${entry.locationId}`,
    provisioned: Boolean(entry.provisioned),
    shortCode: entry.shortCode ?? null,
    slaExists: Boolean(entry.slaExists),
  }));
};
